import type { IQuestion } from '@repo/types/questions/Question.interface';
import type {
  AnswerQuestionVoice,
  AnswerUserQuestionVoice,
  ExportAnswerVoice,
  FeedbackAnswerQuestionVoice,
  QuestionVoiceContent,
  QuestionVoiceData,
  QuestionVoiceSerialized,
} from '@repo/types/questions/QuestionVoice';
import type { CorrectType } from '@repo/types/quiz';

import { QuestionType } from '@repo/types/enums/questionType';

export class QuestionVoice implements IQuestion {
  readonly type: QuestionType;
  readonly content: string;
  private readonly points: number = 0;
  private answer: AnswerQuestionVoice = null;
  private selectedAnswer: AnswerUserQuestionVoice | null = null;
  private marked: FeedbackAnswerQuestionVoice | null = null;

  constructor(data: QuestionVoiceData, selectedAnswer: AnswerUserQuestionVoice | null, marked: FeedbackAnswerQuestionVoice | null) {
    this.type = QuestionType.VOICE;
    this.content = data.content;
    this.points = data.points;
    this.answer = data.answer;
    this.selectedAnswer = selectedAnswer;
    this.marked = marked;
  }

  getContent(): QuestionVoiceContent {
    return {
      content: this.content,
      answer: this.answer,
    };
  }

  getMaxPoints(): number {
    return this.points;
  }

  getPoints(): number {
    if (this.marked == null || this.marked.points == null) return 0;

    return this.marked.points;
  }

  setAnswer(answer: string): void {
    this.selectedAnswer = { answer: answer };
  }
  getAnswer(): AnswerUserQuestionVoice | null {
    return this.selectedAnswer;
  }

  isAnswered(): boolean {
    return this.selectedAnswer != null;
  }

  resetAnswer(): void {
    this.selectedAnswer = null;
  }

  setMarked(marked: FeedbackAnswerQuestionVoice | null): void {
    this.marked = marked;
  }

  getMarked(): FeedbackAnswerQuestionVoice | null {
    return this.marked;
  }

  isMarked(): boolean {
    return !(this.marked == null);
  }
  correctState(): CorrectType {
    if (!this.isAnswered()) return null;

    const marked = this.getMarked();
    /*
      Voice answers are always graded by hand, so an answer without feedback
      yet is awaiting a grade — not incorrect.
    */
    if (marked == null) return 'waiting';
    if (marked.manualCheck && !marked.marked) return 'waiting';

    const points: number = this.getPoints();
    const maxPoints: number = this.getMaxPoints();

    if (points === 0) return false;
    return points === maxPoints ? true : 'partially';
  }

  toJSON(): QuestionVoiceSerialized {
    return {
      marked: this.marked,
      selectedAnswer: this.selectedAnswer,
    };
  }

  importAnswer(answer: ExportAnswerVoice): void {
    if (answer.answer) {
      this.selectedAnswer = { answer: answer.answer };
    } else {
      this.selectedAnswer = null;
    }
  }

  exportAnswer(): ExportAnswerVoice {
    return { answer: this.selectedAnswer?.answer || null };
  }
}
